import { DiscountScope } from '@squareup/sq-addons-web-sdk';

export function isDiscountUpdated(discount) {
  return (
    (discount.scope === DiscountScope.ORDER &&
      discount.amountMoney?.amount !== discount.appliedMoney?.amount) ||
    discount.appliedMoney?.amount === 0
  );
}

/**
 * Returns the modifier UID and line item UID for a modifier discount. If
 * not a modifier discount (e.g. line item discount), returns null.
 *
 * @param discountReferenceLine Discount referenceLine which references uid.
 *   For line item discount, this will be the line item UID. For modifier
 *   discount, this will be the modifier UID.
 * @param lineItems Array of lineItems from the order
 * @returns Object with values representing the line items id and mod id
 */
export function getLineItemAndModifierUidsForModifierDiscount(
  discountReferenceLine,
  lineItems = []
) {
  for (const lineItem of lineItems) {
    for (const modifier of lineItem?.modifiers ?? []) {
      if (modifier?.uid === discountReferenceLine) {
        return {
          lineItemId: lineItem.uid,
          modifierId: modifier.uid,
        };
      }
    }
  }
  return null;
}

/**
 *
 * @param orderDiscounts Array of discounts from Square order.
 * @returns Array of reduced objects by pxCompCardId containing
 *          compCardId and compDiscounts
 */
export function getCompCardsFromOrder(orderDiscounts) {
  const compDiscounts = orderDiscounts
    ?.filter((discount) => discount.metadata?.pxCompCardId !== undefined)
    ?.reduce((acc, discount) => {
      const cardId = discount.metadata.pxCompCardId;
      const value = {
        amount: Number(discount?.metadata?.pxQuantityRedeemed ?? 0),
        walletCode: Number(discount?.metadata?.pxWalletCode),
        squareDiscountId: discount?.uid,
      };

      if (!acc[cardId]) {
        acc[cardId] = []; // Initialize as an array
      }
      acc[cardId].push(value); // Push the value directly into the array

      return acc;
    }, {});

  // Send them out as an array of objects by cardId and the value
  return Object.entries(compDiscounts ?? []).map(([key, value]) => ({
    compCardId: key,
    compDiscounts: value,
  }));
}

/** * Sanitizes a check object by removing discount items that are not
 * applicable to the redeem-only operation and removing any tenderItems
 * that can throw off check service calculation while adjusting the totals.
 *
 * @param {Object} check - The check object to sanitize.
 * @param {Array} discountUidsToRemove - Array of discount UIDs to remove.
 * @returns {Object} The sanitized check object.
 */
export function sanitizeRedeemOnlyCheck(check, discountUidsToRemove) {
  const sanitizedCheck = structuredClone(check);

  const discountItemsToRemove = (sanitizedCheck.items || []).filter(
    (item) =>
      item.type === 'discountItem' &&
      discountUidsToRemove.includes(item.referenceLine)
  );

  sanitizedCheck.items = (sanitizedCheck.items || []).filter(
    (item) =>
      !(
        (item.type === 'discountItem' &&
          discountUidsToRemove.includes(item.referenceLine)) ||
        item.type === 'tenderItem'
      )
  );

  const removedDiscountTotal = Math.abs(
    discountItemsToRemove.reduce(
      (sum, item) => sum + Number(item.price || 0),
      0
    )
  );

  sanitizedCheck.totals.discountTotal =
    Number(sanitizedCheck?.totals?.discountTotal ?? 0) + removedDiscountTotal;

  sanitizedCheck.totals.checkTotal =
    Number(sanitizedCheck?.totals?.checkTotal ?? 0) + removedDiscountTotal;

  sanitizedCheck.totals.subTotal =
    Number(sanitizedCheck?.totals?.subTotal ?? 0) + removedDiscountTotal;

  sanitizedCheck.totals.tenderTotal = 0; // Prevent incorrect check service calculation for comp card usage

  return sanitizedCheck;
}
